import { LoadingButton } from "@mui/lab";
import { Box, Button, Stack, Typography } from "@mui/material";
import copy from "fast-copy";
import { useSnackbar } from "notistack";
import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { BreadScrumHeader, CollapsableHeader, MyPaper, TextField } from "../../../../components";
import { useAppDispatch, useAppSelector } from "../../../../redux";
import { useDeleteAttachmentMutation, useUpdateQuoteMutation, useUploadAttachmentToS3Mutation } from "../../../../redux/services";
import { closeBackdrop, openBackdrop } from "../../../../redux/slices/backdrop";
import { Attachment } from "../../../../redux/slices/material";
import { QuoteSliceInterface, updateQuoteSlice, updateQuoteSliceByKeyValue } from "../../../../redux/slices/quote";
import { AppRoutes } from "../../../../router/routes";
import { getImageDimension } from "../../../../utils";
import { preparePayload } from "../../utils";
import { CoverPageImageUpload } from "./imageConfigure";

export const ConfigureCover: React.FC<{ quoteId: string, readOnly: boolean, setIsSaved: any, refetch: any, attachments: any[], setAttachments: any }> = ({ readOnly, ...props }) => {
    const { attachments, setAttachments } = props;

    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const { enqueueSnackbar } = useSnackbar();
    const [searchParams, setSearchParams] = useSearchParams();

    const { quote } = useAppSelector(store => store);
    const { cover_page_config } = quote as QuoteSliceInterface;

    const [uploadImage, uploadImageData] = useUploadAttachmentToS3Mutation();
    const [updateQuote, updatedQuoteData] = useUpdateQuoteMutation();
    const [deleteAttachment, deleteResponse] = useDeleteAttachmentMutation();

    const [triggerValidation, setTriggerValidation] = React.useState(false);

    const [errors, setErrors] = React.useState({
        logo: {
            widthError: false,
            heightError: false,
            xPositionError: false,
            yPositionError: false,
        },
        header: { contentError: false },
    });

    const [upload, setUpload] = React.useState<any>(null);

    const giveMeErrorMessage = () => {
        let message = `Oops! Something went wrong, Unable to Update Cover Page. Try Again Later!`;
        let errorData: any = updatedQuoteData.error;
        message = errorData?.data?.internal_error ?? errorData?.data?.title ?? message;
        return message;
    };

    const validateData = () => {
        let result = copy(errors);
        let isValid = true;

        if (cover_page_config.include_cover_page) {
            if (!attachments?.[0]) {
                enqueueSnackbar("Please upload the image", { variant: "warning" });
                isValid = false;
            }

            if (!cover_page_config?.logo?.width) {
                result.logo.widthError = true;
                isValid = false;
            } else {
                result.logo.widthError = false;
            }
            if (!cover_page_config?.logo?.height) {
                result.logo.heightError = true;
                isValid = false;
            } else {
                result.logo.heightError = false;
            }

            if (!cover_page_config.heading) {
                result.header.contentError = true;
                isValid = false;
            } else {
                result.header.contentError = false;
            }
        }


        setErrors(result);
        return isValid;
    };

    const saveCoverPageDetails = async () => {
        let attachments: any[] = [];

        if (upload) {
            await uploadImage({ object_id: props.quoteId, object_type: "quote", file: upload }).then((res: any) => {
                if (res.data) {
                    const newState = copy(quote);
                    attachments = copy(res.data.getSignedURL?.data ?? []);

                    if (attachments.length) {
                        attachments[0].url = URL.createObjectURL(upload);
                        attachments[0].status = "saved";
                    }

                    newState.cover_page_config.attachments = res.data.getSignedURL?.data ?? [];
                    dispatch(updateQuoteSlice(newState));
                    setUpload(null);
                } else {
                    const error = res.error?.data?.internal_error ?? res.error?.data?.title ?? `Oops! Something went wrong, Unable to Upload Cover Page Image. Try Again Later!`
                    enqueueSnackbar(error, { variant: "error" })
                    console.log('error:', res.error);
                }
            }).catch((err) => {
                const error = err?.data?.internal_error ?? err?.data?.title ?? `Oops! Something went wrong, Unable to Upload Cover Page Image. Try Again Later!`
                enqueueSnackbar(error, { variant: "error" })
                console.log('error:', err);
            });
        }

        let payload = { ...preparePayload({ ...quote, cover_page_config: { ...quote.cover_page_config, attachments: attachments.length > 0 ? attachments : quote.cover_page_config?.attachments } }) };

        dispatch(openBackdrop('Updating....'));
        updateQuote({ id: props.quoteId, payload }).then((res: any) => {
            if (res.data) {
                props.setIsSaved(true);
                enqueueSnackbar('Quote Cover Page Updated Successfully!', { variant: "success" });
                setTimeout(() => {
                    props.refetch();
                    dispatch(closeBackdrop());
                }, 2000);
            } else {
                console.log('error:', res.error)
                enqueueSnackbar(giveMeErrorMessage(), { variant: "error" })
                dispatch(closeBackdrop());
            }
        }).catch((err) => {
            const error = err?.data?.internal_error ?? err?.data?.title ?? `Oops! Something went wrong, Unable to Update Cover Page. Try Again Later!`
            enqueueSnackbar(error, { variant: "error" })
            console.log('error:', err);
        });

        setSearchParams({});
    };


    const onLogoUploaded = (file: File) => {
        props.setIsSaved(false);
        if (file) {
            dispatch(updateQuoteSliceByKeyValue({ key: "cover_page_config", value: { ...cover_page_config, logo: { ...cover_page_config.logo, src: null, width: 0, height: 0 } } as never }));
            setUpload(file);
            setAttachments([file]);
        } else {
            dispatch(updateQuoteSliceByKeyValue({ key: "cover_page_config", value: { ...cover_page_config, logo: { ...cover_page_config.logo, isActive: false } } as never }));
            setAttachments([]);
        }
    };

    const onDelete = async (file: File | Attachment) => {
        props.setIsSaved(false);
        if ((file as Attachment)?._id) {
            await deleteAttachment({ object_id: props.quoteId, object_type: "quote", attachment_id: (file as Attachment)?._id })
            setAttachments([]);
            props.refetch();
        } else {
            dispatch(updateQuoteSliceByKeyValue({ key: "cover_page_config", value: { ...cover_page_config, logo: { ...cover_page_config.logo, width: 0, height: 0, isActive: false, horizontal: null, vertical: null }, attachments: [] } as never }));
            setUpload(null);
            setAttachments([]);
        }
    };

    React.useEffect(() => {
        if (attachments?.[0] && !cover_page_config.logo?.height && !cover_page_config.logo?.width) {
            let src = (attachments?.[0] as Attachment)?.url || URL.createObjectURL(attachments?.[0] as File);
            if (src) {

                getImageDimension(src, (_, aspectRatio) => {

                    const imageWidth = 100;
                    const imageHeight = Math.ceil(100 / aspectRatio);

                    dispatch(updateQuoteSliceByKeyValue(
                        {
                            key: "cover_page_config",
                            value: {
                                ...cover_page_config,
                                logo: {
                                    ...cover_page_config.logo,
                                    width: imageWidth,
                                    height: imageHeight,
                                    horizontal: 50,
                                    vertical: 50
                                }
                            } as never
                        }))
                });
                props.setIsSaved(false);
            }
        }
        // eslint-disable-next-line
    }, [attachments]);

    React.useEffect(() => {
        if (deleteResponse.isLoading) {
            dispatch(openBackdrop("Deleting Cover Page Image..."))
            return;
        }

        if (deleteResponse.isError) {
            enqueueSnackbar("Unable to delete Cover Page Image.", { variant: "error" })
        }

        if (deleteResponse.isSuccess) {
            enqueueSnackbar("Cover Page Image deleted successfully.", { variant: "success" })
            setUpload(null);
            dispatch(updateQuoteSliceByKeyValue({ key: "cover_page_config", value: { ...cover_page_config, logo: { ...cover_page_config.logo, src: null, width: 0, height: 0, isActive: false, horizontal: null, vertical: null }, attachments: [] } as never }));
            setAttachments([]);
        }

        dispatch(closeBackdrop());
        // eslint-disable-next-line
    }, [deleteResponse.status]);

    React.useEffect(() => {
        if (triggerValidation) {
            validateData();
        }
        // eslint-disable-next-line
    }, [cover_page_config]);

    React.useEffect(() => {
        if (cover_page_config.attachments?.length > 0) {
            setTimeout(() => {
                setAttachments((cover_page_config.attachments) as Attachment[]);
            }, 2000)
        }
        // eslint-disable-next-line
    }, [cover_page_config]);

    React.useEffect(() => {
        if (Boolean(searchParams.get("forceSave"))) {
            saveCoverPageDetails();
        };
        // eslint-disable-next-line
    }, [searchParams]);

    React.useEffect(() => {
        props.setIsSaved(true);
        // eslint-disable-next-line
    }, []);

    const getPrevPath = (): any => {
        if (window.history?.state?.idx > 0) {
            return -1;
        }
        return AppRoutes.viewQuickQuoteBasicDetail(props.quoteId);
    }

    const isLoading = uploadImageData.isLoading || updatedQuoteData.isLoading || deleteResponse.isLoading;

    return (
        (<Stack
            spacing={1}
            sx={{
                position: "relative",
                height: "100%"
            }}>
            {/* Back Button */}
            <Box
                sx={{
                    bgcolor: "white",
                    position: "sticky",
                    top: 1,
                    left: 0,
                    right: 0,
                    zIndex: 99
                }}>
                <BreadScrumHeader
                    openWebNav={false}
                    toogleWebNav={() => false}
                    toogleMobileNav={() => false}
                    history={[]}
                    showBackBtn={true}
                    onBackBtnClick={() => navigate(getPrevPath())}
                    hideMenuBtn={true}
                    currentPath={"Cover Page"}
                />
            </Box>
            <Stack spacing={1} pb={1}>
                {/* Header Style */}
                <MyPaper padding={0}>
                    <CollapsableHeader
                        disabled={readOnly}
                        title={"Cover Page"}
                        showIcon={true}
                        isExpanded={true}
                        showSwitch={true}
                        switchStatus={cover_page_config.include_cover_page}
                        onSwitchChanged={(isChecked) => {
                            dispatch(updateQuoteSliceByKeyValue({ key: "cover_page_config", value: { ...cover_page_config, include_cover_page: isChecked } as never }));
                            props.setIsSaved(false);
                        }}

                    >
                        <Box sx={{
                            p: 1
                        }}>
                            <Stack spacing={1}>
                                <Stack
                                    direction={"row"}
                                    spacing={1}
                                    sx={{
                                        alignItems: "center",
                                        justifyContent: "flex-start"
                                    }}>
                                    <Typography variant="body1" sx={{
                                        fontFamily: "htrts_medium"
                                    }}>
                                        Cover Page Heading
                                    </Typography>
                                </Stack>
                                <TextField
                                    variant="outlined"
                                    placeholder="Enter Heading"
                                    size="small"
                                    multiline
                                    rows={3}
                                    onChange={(e) => {
                                        dispatch(updateQuoteSliceByKeyValue({ key: "cover_page_config", value: { ...cover_page_config, heading: e.target.value } as never }));
                                        props.setIsSaved(false);
                                    }}
                                    value={cover_page_config.heading}
                                    error={errors.header?.contentError || false}
                                    helperText={
                                        errors.header?.contentError ? "Please enter the note" : ""
                                    }
                                    viewOnly={readOnly}
                                />
                            </Stack>
                        </Box>
                    </CollapsableHeader>
                </MyPaper>

                {/* Image Upload */}
                <MyPaper padding={0}>
                    <CoverPageImageUpload
                        title="Cover Page Image"
                        showSwitch={false}
                        switchStatus={cover_page_config.logo?.isActive}
                        onSwitchChanged={(isChecked) => {
                            dispatch(updateQuoteSliceByKeyValue({ key: "cover_page_config", value: { ...cover_page_config, logo: { ...cover_page_config.logo, isActive: isChecked } } as never }));
                            props.setIsSaved(false);
                        }}
                        image={attachments ? attachments : []}
                        onImageUpload={onLogoUploaded}
                        onDelete={onDelete}
                        btnName={"Upload Image"}
                        horizontalPosition={cover_page_config.logo?.horizontal}
                        onHorizontalPositionChange={(position) => {
                            dispatch(updateQuoteSliceByKeyValue({ key: "cover_page_config", value: { ...cover_page_config, logo: { ...cover_page_config.logo, horizontal: position } } as never }));
                            props.setIsSaved(false);
                        }}
                        verticalPosition={cover_page_config.logo?.vertical}
                        onVerticalPositionChange={(position) => {
                            dispatch(updateQuoteSliceByKeyValue({ key: "cover_page_config", value: { ...cover_page_config, logo: { ...cover_page_config.logo, vertical: position } } as never }));
                            props.setIsSaved(false);
                        }}
                        height={cover_page_config.logo?.height}
                        width={cover_page_config.logo?.width}
                        onHeightChange={(height) => {
                            // change width according to aspect ratio
                            const attachment = attachments?.[0];
                            if (attachment) {
                                let src = (attachment as Attachment)?.url || URL.createObjectURL(attachment as File);
                                getImageDimension(src, (_, aspectRatio) => {
                                    dispatch(updateQuoteSliceByKeyValue({ key: "cover_page_config", value: { ...cover_page_config, logo: { ...cover_page_config.logo, width: Math.ceil(height * aspectRatio), height: height } } as never }))
                                });
                                props.setIsSaved(false);
                            }
                        }}
                        onWidthChange={(width) => {
                            // change height according to aspect ratio
                            const attachment = attachments?.[0];
                            if (attachment) {
                                let src = (attachment as Attachment)?.url || URL.createObjectURL(attachment as File);
                                getImageDimension(src, (_, aspectRatio) => {
                                    dispatch(updateQuoteSliceByKeyValue({ key: "cover_page_config", value: { ...cover_page_config, logo: { ...cover_page_config.logo, width: width, height: Math.ceil(width / aspectRatio) } } as never }))
                                });
                                props.setIsSaved(false);
                            }
                        }}
                        error={errors.logo}
                        hasReleased={quote?.released}
                    />
                </MyPaper>
            </Stack>
            {/* Action Section */}
            <Box
                sx={{
                    bgcolor: "white",
                    position: "sticky",
                    bottom: 5,
                    left: 0,
                    right: 0,
                    zIndex: 99
                }}>

                {!readOnly && <MyPaper padding={0}>
                    <Stack
                        direction={"row"}
                        spacing={2}
                        sx={{
                            justifyContent: "end",
                            background: (theme) => theme.palette.background.paper,
                            padding: 1,
                        }}>
                        <LoadingButton
                            variant="contained"
                            onClick={() => {
                                let isValid = validateData();
                                if (!isValid) {
                                    enqueueSnackbar(`Please enter the required fields`, {
                                        variant: "warning",
                                    });
                                    setTriggerValidation(true);
                                } else {
                                    saveCoverPageDetails()
                                }
                            }}
                            loading={isLoading}
                        >
                            Save
                        </LoadingButton>
                        <Button
                            variant="outlined"
                            onClick={() => navigate(getPrevPath())}
                            disabled={isLoading}
                        >
                            Cancel
                        </Button>
                    </Stack>
                </MyPaper>
                }
            </Box>
        </Stack>)
    );
};
